import { useState } from 'react';
import {
  Paper, TextInput, PasswordInput, Button, Title, Text, Stack, Box, Group, ThemeIcon, List, Alert, rem, Anchor, Center, Loader,
} from '@mantine/core';
import { IconSchool, IconChartBar, IconUsers, IconShieldLock, IconDeviceMobile, IconAlertTriangle, IconDeviceDesktop } from '@tabler/icons-react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import api from '../services/api';
import { isInternalRole } from '../lib/permissions';
import { currentSubdomain } from '../lib/tenant';
import { PRODUCT_NAME } from '../lib/brand';
import { SILENT_ERROR } from '../lib/queryMeta';
import { LanguageMenu } from '../components/LanguageMenu';
import { PoweredBy } from '../components/PoweredBy';
import { QURILMA_SESSIYA, qurilmaXatoKaliti } from '../lib/qurilma';

export default function Login() {
  const { t } = useTranslation();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login: doLogin, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // SaaS: subdomen (maktab) brendlash — <slug>.domen.com da maktab nomini ko'rsatamiz
  const slug = currentSubdomain();
  const { data: brand, isError: brandError, isLoading: brandLoading } = useQuery({
    queryKey: ['tenant-brand', slug],
    enabled: !!slug,
    retry: false,
    queryFn: async () => (await api.get<{ name: string; slug: string; isActive: boolean }>(`/tenant/by-slug/${slug}`)).data,
    // Noto'g'ri subdomen — KUTILGAN holat: pastda alohida Alert chiqadi, toast shart emas
    meta: SILENT_ERROR,
  });
  // "Panel faqat ilova" (spec 2026-10-15 §8.2): rejim yoqiq va bu brauzerda qurilma sessiyasi yo'q →
  // forma o'rniga tushuntirish. Endpoint javob bermasa — oddiy forma (server baribir majburlaydi).
  const { data: rejim, isLoading: rejimYuklanmoqda } = useQuery({
    queryKey: ['panel-rejim'],
    retry: false,
    queryFn: async () => (await api.get<{ panelFaqatIlova: boolean }>('/auth/panel-rejim')).data,
    meta: SILENT_ERROR,
  });
  // Qoidaga kirmaydiganlar (o'qituvchilar, platforma superadmini) brauzerdan kiradi — havola formani ochadi
  const [formaOchiq, setFormaOchiq] = useState(false);
  // Ilovadan kelgan (qurilma sessiyasi bor) — rejimdan qat'i nazar forma: javobini kutish shart emas
  const ilovadan = !!localStorage.getItem(QURILMA_SESSIYA);
  const faqatIlova = rejim?.panelFaqatIlova === true && !ilovadan && !formaOchiq;
  const schoolName = brand?.name ?? PRODUCT_NAME;
  // Subdomen mavjud, ammo maktab topilmadi (noto'g'ri manzil) yoki to'xtatilgan → kirish yopiq
  const tenantNotFound = !!slug && brandError;
  const tenantInactive = !!slug && brand?.isActive === false;
  const tenantBlocked = tenantNotFound || tenantInactive;

  const FEATURES = [
    { icon: IconUsers, text: t('login.f1') },
    { icon: IconChartBar, text: t('login.f2') },
    { icon: IconDeviceMobile, text: t('login.f3') },
    { icon: IconShieldLock, text: t('login.f4') },
  ];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tenantBlocked) return;
    setLoading(true);
    try {
      await doLogin(login, password);
      const role = authService.getUser()?.role;
      if (!isInternalRole(role)) {
        logout();
        notifications.show({
          color: 'red',
          title: t('login.denied'),
          message: t('login.deniedMsg'),
        });
        setLoading(false);
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      const d = axios.isAxiosError(err) ? (err.response?.data as Record<string, unknown> | undefined) : undefined;
      const kalit = qurilmaXatoKaliti(d);
      const msg = kalit
        ? t(kalit)
        : axios.isAxiosError(err)
          ? (d?.error as string | undefined) || err.message
          : t('login.error');
      notifications.show({ color: 'red', message: msg });
    } finally {
      setLoading(false);
    }
  };

  // Allaqachon kirgan bo'lsa — render paytida navigate() emas, deklarativ yo'naltirish.
  if (isAuthenticated) return <Navigate to="/" replace />;

  return (
    <Box style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr', background: 'var(--mantine-color-body)' }} className="sata-login-grid">
      {/* Brend paneli (faqat keng ekranda) */}
      <Box
        visibleFrom="md"
        p={48}
        style={{
          background: 'linear-gradient(150deg, #4c3fb8 0%, #6C5CE7 55%, #8d7ce9 100%)',
          color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}
      >
        <Group gap="sm">
          <Box w={44} h={44} style={{ borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconSchool size={26} color="white" />
          </Box>
          <Text fw={800} fz="xl">{schoolName}</Text>
        </Group>

        <Box>
          <Title order={1} c="white" mb="md" style={{ fontSize: rem(38), lineHeight: 1.15 }}>
            {t('login.heroTitle')}
          </Title>
          <Text c="white" opacity={0.85} mb="xl" maw={440}>
            {t('login.heroSubtitle', { school: schoolName })}
          </Text>
          <List spacing="sm" center>
            {FEATURES.map((f) => (
              <List.Item
                key={f.text}
                icon={<ThemeIcon size={28} radius="md" variant="white" color="brand"><f.icon size={16} /></ThemeIcon>}
              >
                <Text c="white" opacity={0.92} size="sm">{f.text}</Text>
              </List.Item>
            ))}
          </List>
        </Box>
      </Box>

      {/* Kirish formasi */}
      <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Stack w={380} maw="100%" gap="lg">
          <Stack gap={4} align="center" hiddenFrom="md">
            <ThemeIcon size={56} radius="lg" color="brand" variant="light"><IconSchool size={30} /></ThemeIcon>
          </Stack>
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Title order={2}>{t('login.welcome')}</Title>
              <Text c="dimmed" size="sm">{slug ? schoolName : t('login.subtitle', { school: PRODUCT_NAME })}</Text>
            </Stack>
            <LanguageMenu />
          </Group>

          <Paper withBorder shadow="sm" radius="lg" p="xl">
            {tenantBlocked && (
              <Alert color={tenantInactive ? 'orange' : 'red'} icon={<IconAlertTriangle size={18} />} mb="md" variant="light">
                {tenantInactive ? t('login.tenantInactive') : t('login.tenantNotFound')}
              </Alert>
            )}
            {rejimYuklanmoqda && !ilovadan ? (
              // Rejim kelguncha forma ko'rsatilmaydi (forma → "faqat ilova" miltillashi bo'lmasin); xato → forma.
              // Qurilma sessiyasi bo'lsa forma baribir chiqadi — Loader shart emas (endpoint osilsa ham forma ko'rinadi)
              <Center py="xl"><Loader /></Center>
            ) : faqatIlova ? (
              <Stack align="center" ta="center" gap="sm">
                <ThemeIcon size={56} radius="xl" variant="light" color="brand"><IconDeviceDesktop size={30} /></ThemeIcon>
                <Title order={4}>{t('qurilma.faqatIlovaSarlavha')}</Title>
                <Text size="sm" c="dimmed">{t('qurilma.faqatIlovaMatn')}</Text>
                <Stack gap={2} align="center" mt="sm">
                  <Anchor component="button" type="button" size="sm" onClick={() => setFormaOchiq(true)}>
                    {t('qurilma.loginParolBilan')}
                  </Anchor>
                  <Text size="xs" c="dimmed">{t('qurilma.loginParolIzoh')}</Text>
                </Stack>
              </Stack>
            ) : (
              <form onSubmit={submit}>
                <Stack>
                  <TextInput label={t('login.loginLabel')} placeholder="admin" size="md" value={login} onChange={(e) => setLogin(e.currentTarget.value)} required autoFocus disabled={tenantBlocked} />
                  <PasswordInput label={t('login.passwordLabel')} placeholder="••••••••" size="md" value={password} onChange={(e) => setPassword(e.currentTarget.value)} required disabled={tenantBlocked} />
                  <Button type="submit" fullWidth size="md" mt="xs" loading={loading || brandLoading} disabled={tenantBlocked}>{t('login.loginBtn')}</Button>
                </Stack>
              </form>
            )}
          </Paper>

          {/* Huquqiy havolalar — kirmasdan ham ochiladi */}
          <Group justify="center" gap={6} wrap="wrap">
            <Anchor component={Link} to="/terms" size="xs" c="dimmed">{t('legal.terms')}</Anchor>
            <Text size="xs" c="dimmed">·</Text>
            <Anchor component={Link} to="/privacy" size="xs" c="dimmed">{t('legal.privacy')}</Anchor>
            <Text size="xs" c="dimmed">·</Text>
            <Anchor component={Link} to="/cookies" size="xs" c="dimmed">{t('legal.cookies')}</Anchor>
          </Group>

          {/* Brend muhri — formaning yonida, ya'ni brend paneli yashiringan
              tor ekranda ham ko'rinadi (ilgari `visibleFrom="md"` blokda edi). */}
          <PoweredBy />
        </Stack>
      </Box>
    </Box>
  );
}
